/**
 * Platform side of the editor-canvas protocol: strict, fail-closed parsing of
 * inbound (canvas -> Platform) messages. Wire contract lives in
 * `../editorProtocolContract` (shared byte-for-byte with the Website Module).
 *
 * This module only validates *shape*. Whether ids exist in the currently
 * loaded draft is decided by the frame against Platform state, which stays the
 * single authority.
 */
import { GRID_COLUMNS } from '../grid';
import type { GridPlacement, ViewportKey } from '../types';
import {
  CHROME_KINDS,
  DRAG_PLACEMENT_FIELDS,
  EDITOR_MESSAGES,
  EDITOR_PROTOCOL_VERSION,
  ENVELOPE_FIELDS,
  LAYERS,
  MAX_ID_LENGTH,
  MAX_TEXT_LENGTH,
  RESIZE_PLACEMENT_FIELDS,
  SEMANTIC_VIEWPORTS,
  SELECTION_KINDS,
  type FieldKind,
} from '../editorProtocolContract';

export type EditorSelection =
  | { kind: 'none' }
  | { kind: 'block'; id: string }
  | { kind: 'section'; id: string }
  | { kind: 'header' }
  | { kind: 'footer' };

export interface EditorViewportPayload {
  name: ViewportKey;
  width: number;
  height: number;
}

export type ParsedCanvasMessage = { type: string; [key: string]: unknown };

export type ParseResult =
  | { ok: true; message: ParsedCanvasMessage }
  | { ok: false; reason: string };

const fail = (reason: string): ParseResult => ({ ok: false, reason });

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isId(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= MAX_ID_LENGTH;
}

function isInt(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= Number.MAX_SAFE_INTEGER;
}

const includes = (list: readonly string[], value: unknown): boolean => typeof value === 'string' && list.includes(value);

/** Validates a placement patch. `allowed` limits which fields a drag/resize may touch. */
export function validatePlacement(
  value: unknown,
  viewport: ViewportKey,
  allowed: readonly string[],
): Partial<GridPlacement> | null {
  if (!isRecord(value)) return null;
  const keys = Object.keys(value);
  if (keys.length === 0 || keys.some((key) => !allowed.includes(key))) return null;
  const columns = GRID_COLUMNS[viewport];
  const out: Partial<GridPlacement> = {};
  for (const key of keys) {
    const raw = value[key];
    switch (key) {
      case 'gridStart':
        if (!Number.isInteger(raw) || (raw as number) < 1 || (raw as number) > columns) return null;
        out.gridStart = raw as number;
        break;
      case 'gridSpan':
        if (!Number.isInteger(raw) || (raw as number) < 1 || (raw as number) > columns) return null;
        out.gridSpan = raw as number;
        break;
      case 'offsetX':
      case 'offsetY':
        if (typeof raw !== 'number' || !Number.isFinite(raw) || Math.abs(raw) > 1200) return null;
        out[key] = raw;
        break;
      case 'width':
        if (typeof raw !== 'number' || !Number.isFinite(raw) || raw <= 0 || raw > 2000) return null;
        out.width = raw;
        break;
      case 'height':
        if (typeof raw !== 'number' || !Number.isFinite(raw) || raw <= 0 || raw > 1200) return null;
        out.height = raw;
        break;
      case 'layer':
        if (!includes(LAYERS, raw)) return null;
        out.layer = raw as GridPlacement['layer'];
        break;
      default:
        return null;
    }
  }
  if (out.gridStart !== undefined && out.gridSpan !== undefined && out.gridStart + out.gridSpan - 1 > columns) return null;
  return out;
}

export function validateSelection(value: unknown): EditorSelection | null {
  if (!isRecord(value) || !includes(SELECTION_KINDS, value.kind)) return null;
  const kind = value.kind as EditorSelection['kind'];
  if (kind === 'block' || kind === 'section') {
    return isId(value.id) && Object.keys(value).length === 2 ? { kind, id: value.id } : null;
  }
  return Object.keys(value).length === 1 ? ({ kind } as EditorSelection) : null;
}

function validateField(kind: FieldKind, value: unknown, message: Record<string, unknown>): boolean {
  switch (kind) {
    case 'string':
      return isId(value);
    case 'text':
      return typeof value === 'string' && value.length <= MAX_TEXT_LENGTH;
    case 'int':
      return isInt(value);
    case 'object':
      return isRecord(value);
    case 'chrome':
      return includes(CHROME_KINDS, value);
    case 'gridViewport':
      return includes(SEMANTIC_VIEWPORTS, value);
    case 'selection':
      return validateSelection(value) !== null;
    case 'viewport':
      return isRecord(value) && includes(SEMANTIC_VIEWPORTS, value.name) && isInt(value.width) && (value.width as number) > 0 && isInt(value.height);
    case 'position':
      return isRecord(value) && isId(value.sectionId) && isId(value.columnId) && isInt(value.index) && Object.keys(value).length === 3;
    case 'placement': {
      const viewport = message.viewport as ViewportKey;
      const allowed = message.type === 'zoea:editor-canvas:resize-block' ? RESIZE_PLACEMENT_FIELDS : DRAG_PLACEMENT_FIELDS;
      return includes(SEMANTIC_VIEWPORTS, viewport) && validatePlacement(value, viewport, allowed) !== null;
    }
    default:
      return false;
  }
}

/**
 * Parses one raw `MessageEvent.data` coming from the canvas. Origin/source
 * checks belong to the caller; this enforces version, session lifecycle and
 * shape. Unknown types, wrong direction, missing or extra-typed fields fail.
 */
export function parseCanvasMessage(data: unknown, expected: { nonce: string; session: string }): ParseResult {
  if (!isRecord(data)) return fail('not-an-object');
  if (typeof data.type !== 'string') return fail('missing-type');
  if (data.protocolVersion !== EDITOR_PROTOCOL_VERSION) return fail('unsupported-protocol-version');
  if (data.nonce !== expected.nonce) return fail('wrong-nonce');
  if (data.session !== expected.session) return fail('stale-session');
  const spec = EDITOR_MESSAGES[data.type];
  if (!spec) return fail('unknown-message');
  if (spec.direction !== 'canvas-to-platform') return fail('wrong-direction');
  for (const [field, kind] of Object.entries(ENVELOPE_FIELDS)) {
    if (field === 'type' || field === 'protocolVersion') continue;
    if (!validateField(kind, data[field], data)) return fail(`invalid-${field}`);
  }
  for (const [field, kind] of Object.entries(spec.required)) {
    if (!(field in data) || !validateField(kind, data[field], data)) return fail(`invalid-${field}`);
  }
  const allowedKeys = new Set([...Object.keys(ENVELOPE_FIELDS), ...Object.keys(spec.required)]);
  if (Object.keys(data).some((key) => !allowedKeys.has(key))) return fail('unexpected-field');
  return { ok: true, message: data as ParsedCanvasMessage };
}
